import { useEffect, useState } from 'react';
import { Crown, Sparkles, Star } from 'lucide-react';

/**
 * LevelUpOverlay — PUBG/Free-Fire style rank-up celebration.
 *
 * Iter 137: extended choreography (≈5.6s total):
 *   0.00s  Full-screen flash of tier color + camera shake
 *   0.15s  Radial god-ray burst behind the tier badge
 *   0.30s  Tier badge zooms in with a snap/bounce
 *   0.60s  Confetti + tier-colored sparks eject from center
 *   0.80s  "RANK UP" text slams in with a bold glow
 *   1.10s  Tier name (e.g. "DIAMOND") slides in under the badge
 *   1.60s  From → arrow → To rank row fades in
 *   1.90s  "PERKS UNLOCKED" panel slides up listing the new benefits
 *   5.20s  Everything gently fades out
 *
 * data shape:
 *   {
 *     from: "Bronze", to: "Silver", color: "#C0C0C0", emoji: "🥈",
 *     benefits: ["+10% daily bonus", ...],   // optional but recommended
 *     dailyBonusPct: 10,                       // optional
 *   }
 */
export default function LevelUpOverlay({ data, onClose }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (!data) return;
    setMounted(true);
    const t = setTimeout(() => { setMounted(false); onClose?.(); }, 5600);
    return () => clearTimeout(t);
  }, [data, onClose]);

  if (!data || !mounted) return null;

  const color = data.color || '#D4AF37';
  const toName = (data.to || '').toString();
  const fromName = (data.from || '').toString();
  const benefits = Array.isArray(data.benefits) ? data.benefits.slice(0, 5) : [];

  // Generate confetti shards once per mount (deterministic per render).
  const shards = Array.from({ length: 36 }, (_, i) => {
    const angle = (i / 36) * Math.PI * 2;
    const dist = 200 + Math.random() * 220;
    return {
      id: i,
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist,
      rot: Math.random() * 720 - 360,
      delay: 0.6 + Math.random() * 0.4,
      size: 4 + Math.random() * 7,
      color: i % 4 === 0 ? '#FFFFFF' : i % 4 === 1 ? color : i % 4 === 2 ? '#FFD700' : `${color}cc`,
    };
  });

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center overflow-hidden"
      style={{
        background: 'radial-gradient(circle at center, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.96) 70%)',
        animation: 'lvl-overlay-fade 5.6s ease forwards, lvl-shake 0.55s cubic-bezier(.36,.07,.19,.97) both',
      }}
      onClick={() => { setMounted(false); onClose?.(); }}
      data-testid="level-up-overlay"
    >
      {/* Flash */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: color, animation: 'lvl-flash 0.45s ease-out forwards' }}
      />

      {/* Rotating god-rays */}
      <div
        className="absolute inset-0 flex items-center justify-center pointer-events-none"
        style={{ animation: 'lvl-rays-spin 5.6s linear', opacity: 0 }}
      >
        <div
          className="w-[140%] h-[140%]"
          style={{
            background:
              `conic-gradient(from 0deg, transparent 0deg, ${color}33 8deg, transparent 16deg, transparent 40deg, ${color}33 48deg, transparent 56deg, transparent 80deg, ${color}33 88deg, transparent 96deg, transparent 120deg, ${color}33 128deg, transparent 136deg, transparent 160deg, ${color}33 168deg, transparent 176deg, transparent 200deg, ${color}33 208deg, transparent 216deg, transparent 240deg, ${color}33 248deg, transparent 256deg, transparent 280deg, ${color}33 288deg, transparent 296deg, transparent 320deg, ${color}33 328deg, transparent 336deg, transparent 360deg)`,
            animation: 'lvl-rays-fadein 0.4s ease 0.2s forwards',
          }}
        />
      </div>

      {/* Rising glow halo */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: 420, height: 420,
          background: `radial-gradient(circle, ${color}44 0%, ${color}11 40%, transparent 70%)`,
          animation: 'lvl-halo 5.6s ease-out forwards',
        }}
      />

      {/* Badge + tier name */}
      <div className="relative text-center z-10" style={{ animation: 'lvl-card-in 5.6s ease forwards' }}>
        {/* Badge */}
        <div className="relative mx-auto" style={{ width: 140, height: 140 }}>
          <div
            className="absolute inset-0 rounded-full"
            style={{
              background: `conic-gradient(${color}, transparent, ${color}, transparent, ${color})`,
              animation: 'lvl-badge-ring 2.5s linear infinite',
              filter: 'blur(1px)',
            }}
          />
          <div
            className="absolute inset-1 rounded-full flex items-center justify-center"
            style={{
              background: `radial-gradient(circle at 30% 25%, #ffffff22, ${color}22 40%, #000 100%)`,
              border: `3px solid ${color}`,
              boxShadow: `0 0 36px ${color}AA, inset 0 0 24px ${color}55`,
              animation: 'lvl-badge-pop 0.9s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s backwards',
            }}
          >
            {data.emoji ? (
              <span className="text-6xl" style={{ textShadow: `0 0 18px ${color}`, filter: 'drop-shadow(0 2px 4px #000)' }}>{data.emoji}</span>
            ) : (
              <Crown className="w-16 h-16" style={{ color, filter: `drop-shadow(0 0 8px ${color})` }} />
            )}
          </div>
          {/* Orbiting stars */}
          {[0, 1, 2].map(i => (
            <Star
              key={i}
              className="absolute"
              style={{
                width: 14, height: 14,
                top: '50%', left: '50%', marginLeft: -7, marginTop: -7,
                color,
                filter: `drop-shadow(0 0 4px ${color})`,
                animation: `lvl-orbit-${i} 3s linear infinite`,
              }}
              fill={color}
            />
          ))}
        </div>

        {/* LEVEL UP banner */}
        <div
          className="mt-7 inline-block px-8 py-2 font-black tracking-[0.2em]"
          style={{
            color: '#fff',
            fontSize: 34,
            letterSpacing: '0.15em',
            textShadow: `0 0 16px ${color}, 0 0 32px ${color}aa, 0 4px 8px rgba(0,0,0,0.6)`,
            background: `linear-gradient(180deg, ${color}55, ${color}18 40%, transparent 100%)`,
            borderTop: `2px solid ${color}`,
            borderBottom: `2px solid ${color}`,
            animation: 'lvl-title-slam 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) 0.8s backwards',
            fontFamily: '"Impact", "Arial Black", sans-serif',
          }}
          data-testid="level-up-title"
        >
          RANK UP
        </div>

        {/* Tier name */}
        <h2
          className="mt-5 font-black"
          style={{
            color,
            fontSize: 42,
            letterSpacing: '0.08em',
            textShadow: `0 0 24px ${color}, 0 0 48px ${color}88, 0 3px 0 #000`,
            animation: 'lvl-tier-name-in 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) 1.1s backwards',
            textTransform: 'uppercase',
          }}
          data-testid="level-up-tier-name"
        >
          {toName}
        </h2>

        {/* From → To row */}
        {fromName && (
          <div
            className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-300"
            style={{ animation: 'lvl-fromto-in 0.5s ease 1.6s backwards' }}
          >
            <span className="opacity-60">{fromName}</span>
            <span style={{ color }}>→</span>
            <span className="font-bold" style={{ color }}>{toName}</span>
          </div>
        )}

        {/* Perks unlocked panel — Iter 137: lists the new tier's benefits
            so the player knows exactly what they just earned. */}
        {benefits.length > 0 && (
          <div
            className="mt-5 mx-auto max-w-xs rounded-xl p-3 backdrop-blur-md"
            style={{
              background: `linear-gradient(135deg, ${color}1a, rgba(0,0,0,0.55))`,
              border: `1px solid ${color}55`,
              boxShadow: `0 0 28px ${color}33`,
              animation: 'lvl-perks-slide 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) 1.9s backwards',
            }}
            data-testid="level-up-perks"
          >
            <p
              className="text-[10px] uppercase tracking-[0.2em] font-black mb-2 text-center"
              style={{ color, textShadow: `0 0 8px ${color}88` }}
            >
              Perks Unlocked
            </p>
            <ul className="space-y-1.5 text-left">
              {benefits.map((b, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[12px]"
                  style={{
                    animation: `lvl-perk-item ${0.4}s ease ${2.1 + i * 0.12}s backwards`,
                  }}
                >
                  <span
                    className="mt-0.5 flex-shrink-0 w-1.5 h-1.5 rounded-full"
                    style={{ background: color, boxShadow: `0 0 6px ${color}` }}
                  />
                  <span className="text-gray-100">{b}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div
          className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-semibold text-gray-400 uppercase tracking-wider"
          style={{ animation: 'lvl-fromto-in 0.5s ease 2.4s backwards' }}
        >
          <Sparkles className="w-3.5 h-3.5" style={{ color }} />
          Tap anywhere to dismiss
        </div>
      </div>

      {/* Confetti shards */}
      {shards.map(s => (
        <div
          key={s.id}
          className="absolute top-1/2 left-1/2 pointer-events-none rounded-sm"
          style={{
            width: s.size, height: s.size * 2,
            background: s.color,
            boxShadow: `0 0 6px ${s.color}`,
            '--dx': `${s.dx}px`,
            '--dy': `${s.dy}px`,
            '--rot': `${s.rot}deg`,
            animation: `lvl-shard 1.6s cubic-bezier(0.22, 0.8, 0.27, 1) ${s.delay}s both`,
            transformOrigin: 'center',
          }}
        />
      ))}

      <style>{`
        @keyframes lvl-overlay-fade {
          0%, 92% { opacity: 1; }
          100% { opacity: 0; pointer-events: none; }
        }
        @keyframes lvl-flash {
          0% { opacity: 0.95; }
          100% { opacity: 0; }
        }
        @keyframes lvl-rays-fadein {
          to { opacity: 0.6; }
        }
        @keyframes lvl-rays-spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes lvl-halo {
          0% { transform: scale(0.1); opacity: 0; }
          25% { transform: scale(1); opacity: 1; }
          75% { transform: scale(1.2); opacity: 0.6; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @keyframes lvl-card-in {
          0% { opacity: 0; transform: translateY(8px); }
          12% { opacity: 1; transform: translateY(0); }
          90% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-8px); }
        }
        @keyframes lvl-badge-pop {
          0% { transform: scale(0); opacity: 0; }
          60% { transform: scale(1.25); opacity: 1; }
          100% { transform: scale(1); }
        }
        @keyframes lvl-badge-ring {
          to { transform: rotate(360deg); }
        }
        @keyframes lvl-title-slam {
          0% { transform: scale(3) skewX(-15deg); opacity: 0; filter: blur(12px); }
          60% { transform: scale(0.92) skewX(0); filter: blur(0); opacity: 1; }
          80% { transform: scale(1.04); }
          100% { transform: scale(1); }
        }
        @keyframes lvl-tier-name-in {
          0% { opacity: 0; transform: translateY(20px) scale(0.8); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes lvl-fromto-in {
          0% { opacity: 0; transform: translateY(10px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes lvl-shard {
          0% { transform: translate(-50%, -50%) rotate(0deg) scale(0); opacity: 1; }
          30% { opacity: 1; }
          100% { transform: translate(calc(-50% + var(--dx)), calc(-50% + var(--dy))) rotate(var(--rot)) scale(1); opacity: 0; }
        }
        @keyframes lvl-shake {
          0%, 100% { transform: translate3d(0, 0, 0); }
          10% { transform: translate3d(-6px, -3px, 0); }
          20% { transform: translate3d(7px, 2px, 0); }
          30% { transform: translate3d(-5px, 4px, 0); }
          40% { transform: translate3d(4px, -3px, 0); }
          50% { transform: translate3d(-3px, 2px, 0); }
          60% { transform: translate3d(2px, -2px, 0); }
          70% { transform: translate3d(-2px, 1px, 0); }
          80% { transform: translate3d(1px, 0, 0); }
        }
        @keyframes lvl-perks-slide {
          0% { opacity: 0; transform: translateY(40px) scale(0.95); }
          60% { transform: translateY(-4px) scale(1.02); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes lvl-perk-item {
          0% { opacity: 0; transform: translateX(-12px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes lvl-orbit-0 {
          0% { transform: rotate(0deg) translateX(90px) rotate(0deg); }
          100% { transform: rotate(360deg) translateX(90px) rotate(-360deg); }
        }
        @keyframes lvl-orbit-1 {
          0% { transform: rotate(120deg) translateX(90px) rotate(-120deg); }
          100% { transform: rotate(480deg) translateX(90px) rotate(-480deg); }
        }
        @keyframes lvl-orbit-2 {
          0% { transform: rotate(240deg) translateX(90px) rotate(-240deg); }
          100% { transform: rotate(600deg) translateX(90px) rotate(-600deg); }
        }
      `}</style>
    </div>
  );
}
